import { useEffect, useState } from "react";
import { X, Loader2, ExternalLink, Check, AlertTriangle, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  getDiscordMembership,
  linkDiscord,
  syncDiscord,
  type DiscordMembershipStatus,
} from "../services/DiscordService";
import { membershipQueryKey } from "../hooks/useDiscordGate";

interface DiscordStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DISCORD_INVITE_URL = "https://discord.gg/3DTMngFYb9";

const DiscordIcon = ({ size = 18, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z" />
  </svg>
);

type ModalState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; status: DiscordMembershipStatus };

interface CTACopy {
  label: string;
  action: "link" | "sync" | "open";
  tone: "primary" | "warning";
}

function ctaForStatus(status: DiscordMembershipStatus): CTACopy {
  if (!status.linked) return { label: "Link with Discord", action: "link", tone: "primary" };
  if (status.ok) return { label: "Go to Discord", action: "open", tone: "primary" };
  return { label: "Sync with Discord", action: "sync", tone: "warning" };
}

interface RowProps {
  ok: boolean;
  label: string;
  detail: string;
  loading?: boolean;
}

function StatusRow({ ok, label, detail, loading }: RowProps) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div
        className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
          loading
            ? "bg-gray-200 dark:bg-dark-700"
            : ok
            ? "bg-green-100 dark:bg-green-900/40"
            : "bg-amber-100 dark:bg-amber-900/40"
        }`}
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin text-gray-500 dark:text-dark-300" />
        ) : ok ? (
          <Check className="h-3 w-3 text-green-700 dark:text-green-300" />
        ) : (
          <AlertTriangle className="h-3 w-3 text-amber-700 dark:text-amber-300" />
        )}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
        <div className="text-xs text-gray-600 dark:text-dark-300">{detail}</div>
      </div>
    </div>
  );
}

export default function DiscordStatusModal({ isOpen, onClose }: DiscordStatusModalProps) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ModalState>({ kind: "loading" });
  const [isActing, setIsActing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setState({ kind: "loading" });
    getDiscordMembership()
      .then((status) => {
        if (!cancelled) setState({ kind: "ready", status });
      })
      .catch((err) => {
        console.error("[DiscordStatusModal] membership fetch failed:", err);
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const status = state.kind === "ready" ? state.status : null;
  const cta = status ? ctaForStatus(status) : null;
  const loading = state.kind === "loading";

  const handlePrimary = async () => {
    if (!cta) return;
    if (cta.action === "open") {
      window.open(DISCORD_INVITE_URL, "_blank");
      onClose();
      return;
    }
    setIsActing(true);
    try {
      if (cta.action === "link") {
        const response = await linkDiscord();
        window.location.href = response.redirectUrl;
        return;
      }

      await syncDiscord();
      void queryClient.invalidateQueries({ queryKey: membershipQueryKey });
      const nextStatus = await getDiscordMembership();
      queryClient.setQueryData(membershipQueryKey, nextStatus);
      setState({ kind: "ready", status: nextStatus });
      toast.success("Discord synced successfully.");
      setIsActing(false);
    } catch (err) {
      console.error("[DiscordStatusModal] linkDiscord failed:", err);
      toast.error("Couldn't start Discord sync. Please try again.");
      setIsActing(false);
    }
  };

  const headline = loading
    ? "Checking your Discord status..."
    : state.kind === "error"
    ? "Couldn't load Discord status"
    : status!.ok
    ? "You're all set on Discord"
    : !status!.linked
    ? "Discord not linked"
    : "Discord needs syncing";

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onPointerDown={onClose}
    >
      <div
        className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-6 shadow-2xl"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <DiscordIcon size={24} className="text-[#5865F2]" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Discord Status</h2>
          </div>
          <button
            type="button"
            onPointerDown={onClose}
            className="text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <p className="text-sm text-gray-700 dark:text-dark-200 mb-3">{headline}</p>

        <div className="rounded-lg border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900/40 p-3 mb-5 divide-y divide-gray-200 dark:divide-dark-700">
          <StatusRow
            loading={loading}
            ok={!!status?.linked}
            label="Account linked"
            detail={
              loading
                ? "Looking up your Discord account..."
                : status?.linked
                ? "Your Discord account is linked to DealHawk."
                : "Link your Discord to access member channels."
            }
          />
          <StatusRow
            loading={loading}
            ok={!!status?.inGuild}
            label="Joined the DealHawk server"
            detail={
              loading
                ? "Checking server membership..."
                : status?.inGuild
                ? "You're a member of the DealHawk Discord server."
                : status?.linked
                ? "You haven't joined the server yet. Sync to be added automatically."
                : "Once linked, you'll be added to the DealHawk server."
            }
          />
          <StatusRow
            loading={loading}
            ok={!!status?.hasRole}
            label="Member role granted"
            detail={
              loading
                ? "Checking role permissions..."
                : status?.hasRole
                ? "You have access to all member-only channels."
                : status?.inGuild
                ? "Member role missing — re-sync to refresh permissions."
                : "Joining the server will grant your member role."
            }
          />
        </div>

        {state.kind === "error" && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-800 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>We couldn't reach Discord just now. Please try again in a moment.</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onPointerDown={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onPointerDown={handlePrimary}
            disabled={loading || isActing || !cta}
            className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
              loading || isActing || !cta
                ? "bg-gray-100 dark:bg-dark-700 text-gray-400 dark:text-dark-400 cursor-not-allowed"
                : cta!.tone === "warning"
                ? "bg-amber-500 hover:bg-amber-600 text-white"
                : "bg-[#5865F2] hover:bg-[#4752C4] text-white"
            }`}
          >
            {isActing ? (
              <>
                <Loader2 className="animate-spin h-4 w-4" />
                Syncing...
              </>
            ) : loading ? (
              <>
                <Loader2 className="animate-spin h-4 w-4" />
                Loading...
              </>
            ) : cta?.action === "open" ? (
              <>
                <ExternalLink size={16} />
                {cta.label}
              </>
            ) : cta?.action === "sync" ? (
              <>
                <RefreshCw size={16} />
                {cta.label}
              </>
            ) : cta ? (
              <>
                <DiscordIcon size={16} />
                {cta.label}
              </>
            ) : (
              "Try again"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
